import { createColumnHelper } from '@tanstack/react-table';
import { Edit } from 'lucide-react';

import { Button } from '@@/buttons';
import { Badge } from '@@/Badge';
import { Switch } from '@@/form-components/SwitchField/Switch';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';

import { convertBytesToHumanReadable } from './ThresholdInput';
import { AlertRuleRow } from './types';

export const columnHelper = createColumnHelper<AlertRuleRow>();

export function createColumns(
  onEdit: (alertRule: AlertRuleRow) => void,
  onToggle: (alertRule: AlertRuleRow, enabled: boolean) => void,
  togglingId?: number,
  isAlertManagerEnabled?: boolean
) {
  return [
    columnHelper.accessor('name', {
      header: 'Alert Rule',
      cell: ({ getValue, row }) => {
        const { isInternal, supportedAgentVersion, supportedEnvironmentTypes } =
          row.original;

        return (
          <div className="flex items-center gap-2">
            <span className="font-medium">{getValue()}</span>
            {isInternal && (
              <Badge type="info" className="text-xs">
                System
              </Badge>
            )}
            {supportedEnvironmentTypes && (
              <Badge type="success" className="text-xs">
                {supportedEnvironmentTypes}
              </Badge>
            )}
            {supportedAgentVersion && (
              <Badge type="warn" className="text-xs">
                {supportedAgentVersion}
              </Badge>
            )}
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const alertRule = row.original;
        const isDisabledDueToNoAlertManager =
          !alertRule.enabled && !isAlertManagerEnabled;

        const switchComponent = (
          <Switch
            id={`toggle-alert-rule-${alertRule.id}`}
            name={`toggle-alert-rule-${alertRule.id}`}
            checked={alertRule.enabled}
            onChange={() => onToggle(alertRule, !alertRule.enabled)}
            disabled={
              togglingId === alertRule.id || isDisabledDueToNoAlertManager
            }
            data-cy={`toggle-alert-rule-${alertRule.id}`}
          />
        );

        const message =
          'Enable the internal alert manager in Alert Settings to activate alert rules';
        return (
          <div className="flex gap-2 items-center">
            {isDisabledDueToNoAlertManager ? (
              <TooltipWithChildren message={message} position="top">
                <div className="inline-block cursor-not-allowed">
                  {switchComponent}
                </div>
              </TooltipWithChildren>
            ) : (
              switchComponent
            )}
            {isDisabledDueToNoAlertManager ? (
              <TooltipWithChildren message={message} position="top">
                <div className="inline-block cursor-not-allowed">
                  <Button
                    color="primary"
                    size="xsmall"
                    icon={Edit}
                    onClick={() => onEdit(alertRule)}
                    disabled={
                      !alertRule.isEditable || isDisabledDueToNoAlertManager
                    }
                    data-cy={`edit-alert-rule-${alertRule.id}`}
                  >
                    Edit
                  </Button>
                </div>
              </TooltipWithChildren>
            ) : (
              <Button
                color="primary"
                size="xsmall"
                icon={Edit}
                onClick={() => onEdit(alertRule)}
                disabled={!alertRule.isEditable}
                data-cy={`edit-alert-rule-${alertRule.id}`}
              >
                Edit
              </Button>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor('description', {
      header: 'Description',
    }),
    columnHelper.accessor('severity', {
      header: 'Severity',
    }),
    columnHelper.accessor('threshold', {
      header: 'Threshold',
      cell: ({ getValue, row }) => {
        const threshold = getValue();
        const { metricType } = row.original;

        if (threshold === undefined || threshold === null) {
          return '-';
        }

        // Format based on metric type
        switch (metricType) {
          case 'percentage':
            return `${threshold}%`;
          case 'bytes': {
            const { value, unit } = convertBytesToHumanReadable(threshold);
            return `${value} ${unit}`;
          }
          case 'raw':
          default:
            return threshold;
        }
      },
    }),
    columnHelper.accessor('conditionOperator', {
      header: 'Condition',
      cell: ({ getValue, row }) => {
        const operator = getValue();
        const { threshold } = row.original;

        if (threshold === undefined || threshold === null) {
          return '-';
        }
        return operator || '>';
      },
    }),
    columnHelper.accessor('duration', {
      header: 'Duration (min)',
      cell: ({ getValue }) => {
        const duration = getValue();
        return duration ? `${duration}m` : '-';
      },
    }),
    columnHelper.accessor('createdAt', {
      header: 'Created At',
    }),
  ];
}

export const columns = createColumns(
  () => {},
  () => {},
  undefined,
  false
);
